import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';
import config from '../../config.js';

import '../../styles/global.css';
import '../../styles/requests.css';
import '../../styles/buttons.css';
import '../../styles/status.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader';
import PageTable from '../../components/general/pageTable';
import useAuthenticate from '../../components/authentication/authenticate.js';
import Loading from '../../components/general/loading.js';
import CreateWorkPermitForm from '../../components/forms/createWorkPermitForm.js';
import WorkPermitCards from '../../components/cards/workPermitCards';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';


const RWorkPermits = () => {
    const { user, isLoading } = useAuthenticate();
    const queryClient = useQueryClient();

    const role = Object.keys(user?.user?.active || {})[0];
    const accountId = user?.user?.accounts?.[role];

    const [addOverlayOpen, setAddOverlayOpen] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    const { data: properties } = useQuery({
        queryKey: ['properties'],
        queryFn: fetchProperties,
    });

    const { data: workPermits, isLoading: isTableLoading } = useQuery({
        queryKey: ['workPermits', selectedProperty?.value, accountId, currentPage],
        queryFn: () => fetchWorkPermitsByProperty(selectedProperty?.value, accountId, currentPage, rowsPerPage),
        enabled: !!selectedProperty?.value && !!accountId,
        keepPreviousData: true,
    });

    useEffect(() => {
        if (properties?.length > 0 && !selectedProperty) {
        setSelectedProperty(properties[0]);
        }
    }, [properties, selectedProperty]);

    const handleWorkPermitCreated = () => {
        queryClient.invalidateQueries(['workPermits']);
        setAddOverlayOpen(false);
    };

    const totalPages = workPermits?.totalPages || 1;
    const maxVisibleButtons = 10;
    const startPage = Math.floor((currentPage - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
    const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

    return (
        <div className="full-page w-full">
            <PageHeader pageName="Work Permits" />
    
            {isLoading ? (
                <Loading />
            ) : (
                <>
                    <div className="page-header flex flex-col md:flex-row gap-3">
                        <div className="w-full md:w-[300px]">
                            <Autocomplete
                                options={properties}
                                value={selectedProperty}
                                onChange={(e, newVal) =>
                                    newVal?.value && newVal?.label ? setSelectedProperty(newVal)
                                    : setSelectedProperty(null)
                                }
                                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                getOptionLabel={(option) =>
                                    typeof option === 'string'
                                        ? option
                                        : option?.label || option?.name || ''
                                    }
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Property" variant="outlined" fullWidth />
                                )}
                            />
                        </div>
                        <div className="flex md:flex-row gap-1 w-full md:w-auto mt-2 md:mt-0">
                            <div className="w-full md:w-auto">
                                <button
                                className="header-button w-full md:w-auto h-[50px] md:h-full text-[14px] md:text-[12px]"
                                    onClick={() => setAddOverlayOpen(true)}
                                >
                                    <i className="fas fa-id-card-alt"></i> File Work Permit
                                </button>
                            </div>
                        </div>
                    </div>

                    {isTableLoading ? (
                        <Loading />
                    ) : (
                        <div className="table-wrapper">
                            {/* Mobile View */}
                            <div className="block md:hidden">
                                <WorkPermitCards rows={workPermits?.results || []} />
                            </div>

                            {/* Desktop View */}
                            <div className="hidden md:block">
                                <PageTable
                                rows={workPermits?.results || []}
                                columns={[
                                    {
                                    label: 'Unit',
                                    accessor: (row) => `${row.unit?.unitNumber || 'N/A'}`,
                                    width: '5%',
                                    },
                                    {
                                    label: 'Status',
                                    accessor: (row) => (
                                        <span className={`request-status-badge ${row.status?.toLowerCase()}`}>
                                        {row.status}
                                        </span>
                                    ),
                                    width: '10%'
                                    },
                                    { label: 'Service Provider', accessor: 'details.serviceProvider', width: '12%' },
                                    { label: 'Work Category', accessor: 'details.workCategory', width: '10%' },
                                    { label: 'Title', accessor: 'details.title', width: '20%' },
                                    { label: 'Start Date', accessor: (row) => new Date(row.details.startDate).toLocaleDateString(), width: '10%' },
                                    { label: 'End Date', accessor: (row) => new Date(row.details.endDate).toLocaleDateString(), width: '10%' },
                                ]}
                                hasButtons={false}
                                />
                            </div>
                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="pagination-controls">
                                    <div className="pagination-buttons">
                                        <button
                                            className="pagination-button"
                                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>

                                        {startPage > 1 && (
                                            <button className="pagination-page" onClick={() => setCurrentPage(startPage - 1)}>
                                                ...
                                            </button>
                                        )}
                                        
                                        {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                                            const pageNum = startPage + i;
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`pagination-page ${ currentPage === pageNum ? 'active' : ''}`}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        {endPage < totalPages && (
                                            <button className="pagination-page" onClick={() => setCurrentPage(endPage + 1)} >
                                                ...
                                            </button>
                                        )}

                                        <button
                                            className="pagination-button"
                                            onClick={() =>
                                                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                                            }
                                            disabled={currentPage === totalPages}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {addOverlayOpen && (
                        <CreateWorkPermitForm
                        property={{ property: { _id: selectedProperty.value } }}
                        propertyId={selectedProperty.value}
                        onClose={() => setAddOverlayOpen(false)}
                        onRequestCreated={handleWorkPermitCreated}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const fetchWorkPermitsByProperty = async (propertyId, accountId, page = 1, limit = 10) => {
    if (!propertyId) return { results: [], totalPages: 0, totalResults: 0 };

    try {
        const response = await axios.get(`${config.API_URL}/v2/request/property/${propertyId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            params: { page, limit, sortBy: 'createdAt', sortOrder: 'desc', type: 'WorkPermit' },
        });

        const data = response.data;
        // Filter by resident
        const filteredResults = (data.results || []).filter(
            (r) => r.requestedBy?._id === accountId
        );

        return {
            results: filteredResults,
            totalPages: data.totalPages || 1,
            totalResults: data.totalResults || filteredResults.length,
            page,
        };
    } catch (error) {
        console.error('❌ Error fetching Work Permits for property:', propertyId, error);
        return { results: [], totalPages: 0, totalResults: 0 };
    }
};

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
    return response.data.map(item => ({
    value: item._id,
    label: item.name,
  }));
};

export default RWorkPermits;